/**
 * Party Dialog Component
 *
 * Unified dialog for creating/editing parties (customers/vendors) with:
 * - Entity management integration (accounts-api)
 * - Duplicate detection based on business identifiers
 * - Link to existing entity or create new
 * - Accounting-specific fields (credit terms, linked accounts)
 */

import { useState, useMemo, useEffect } from 'react';


import { PartyAddressFields } from './PartyAddressFields';
import { PartyBankDetails } from './PartyBankDetails';
import { PartyBasicInfo } from './PartyBasicInfo';
import { PartyDuplicateWarning } from './PartyDuplicateWarning';
import { PartyTaxInfo } from './PartyTaxInfo';
import { usePartyDialog } from './usePartyDialog';
import { type CountryIdentifier } from './usePartyDialog-helpers';
import { usePartyDialogEffects } from './usePartyDialogEffects';
import { listRoleDefinitions } from '../../../services/role-definitions-service';
import { ROLE_SUPPLIER, PARTY_ROLES } from '../../../types/billing';
import {
  Modal,
  Button,
  Input,
  Select,
  FormField,
  PhoneInput,
} from '../../ui';
import { CurrencyConfirmDialog } from '../../ui/currency-confirm-dialog';

import type { PartyDialogProps } from './party-dialog-helpers';
import type { RoleOption } from './PartyBasicInfo';
import type { BusinessIdentifierType, State, Country } from '../../../services/geolocation-service';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface DialogContextSetters {
  setBillingStates: React.Dispatch<React.SetStateAction<State[]>>;
  setBillingCountry: React.Dispatch<React.SetStateAction<Country | null>>;
  setLoadingCountries: React.Dispatch<React.SetStateAction<boolean>>;
  setCountriesLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setBusinessIdentifiers: React.Dispatch<React.SetStateAction<BusinessIdentifierType[]>>;
}

interface PartyDialogState {
  billingCountry: Country | null;
  isLoadingCountries: boolean;
  isCountriesLoaded: boolean;
  businessIdentifiers: BusinessIdentifierType[];
  availableRoles: RoleOption[] | undefined;
  setters: DialogContextSetters;
}

function usePartyDialogLocalState(): PartyDialogState {
  const [, setBillingStates] = useState<State[]>([]);
  const [billingCountry, setBillingCountry] = useState<Country | null>(null);
  const [isLoadingCountries, setLoadingCountries] = useState(true);
  const [isCountriesLoaded, setCountriesLoaded] = useState(false);
  const [businessIdentifiers, setBusinessIdentifiers] = useState<BusinessIdentifierType[]>([]);
  const [availableRoles] = useState<RoleOption[] | undefined>(undefined);

  return {
    billingCountry, isLoadingCountries, isCountriesLoaded, businessIdentifiers, availableRoles,
    setters: { setBillingStates, setBillingCountry, setLoadingCountries, setCountriesLoaded, setBusinessIdentifiers },
  };
}

function useRoleDefinitions(orgId: string | undefined, setAvailableRoles: React.Dispatch<React.SetStateAction<RoleOption[] | undefined>>): void {
  useEffect(() => {
    if (!orgId) { return; }
    void listRoleDefinitions(orgId).then(result => {
      if (!result.data) { return; }
      const backendRoles = result.data.filter(r => r.isActive).map(r => ({ value: r.role, label: r.label }));
      const builtInValues = new Set<string>(PARTY_ROLES.map(r => r.value));
      const customRoles = backendRoles.filter(r => !builtInValues.has(r.value));
      setAvailableRoles([...PARTY_ROLES, ...customRoles]);
    });
  }, [orgId, setAvailableRoles]);
}

interface SectionsProps {
  dialog: ReturnType<typeof usePartyDialog>;
  editingParty?: PartyDialogProps['editingParty'];
  availableRoles: RoleOption[] | undefined;
  billingCountry: Country | null;
  countryIdentifiers: CountryIdentifier[];
}

function PartyDialogSections({ dialog, editingParty, availableRoles, billingCountry, countryIdentifiers }: SectionsProps): JSX.Element {
  return (
    <>
      <PartyDuplicateWarning
        duplicateEntities={dialog.duplicateEntities}
        duplicateEntitiesParties={dialog.duplicateEntitiesParties}
        isDuplicateWarningVisible={dialog.isDuplicateWarningVisible}
        selectedEntity={dialog.selectedEntity}
        onCreateNew={dialog.handleCreateNew}
        onLinkToEntity={entity => { void dialog.handleLinkToEntity(entity); }}
        onUnlink={() => dialog.setSelectedEntity(null)}
      />
      <PartyBasicInfo
        availableRoles={availableRoles}
        baseCurrency={dialog.baseCurrency}
        editingParty={editingParty}
        errors={dialog.errors}
        formData={dialog.formData}
        selectedEntity={dialog.selectedEntity}
        setFormData={dialog.setFormData}
      />
      <PartyTaxInfo
        billingCountryName={billingCountry?.name}
        countryIdentifiers={countryIdentifiers}
        errors={dialog.errors}
        formData={dialog.formData}
        selectedEntity={dialog.selectedEntity}
        setFormData={dialog.setFormData}
      />
      <ContactInfoSection errors={dialog.errors} formData={dialog.formData} setFormData={dialog.setFormData} />
      <PartyAddressFields formData={dialog.formData} setFormData={dialog.setFormData} />
      <PaymentTermsSection currencyLocked={!!editingParty?.defaultCurrency} dialog={dialog} editingParty={!!editingParty} />
      {dialog.formData.roles.includes(ROLE_SUPPLIER) && (
        <PartyBankDetails formData={dialog.formData} setFormData={dialog.setFormData} />
      )}
    </>
  );
}

interface PartyDialogWiringResult {
  dialog: ReturnType<typeof usePartyDialog>;
  countryIdentifiers: CountryIdentifier[];
}

function usePartyDialogWiring(props: PartyDialogProps, localState: PartyDialogState): PartyDialogWiringResult {
  const { isOpen, onClose, onSuccess, orgId, ledgerId, partyType, editingParty, existingPartyCodes = [] } = props;
  const stablePartyCodes = useMemo(
    () => existingPartyCodes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existingPartyCodes.join(',')],
  );
  const storageKey = `partyDialog_draft_${partyType}`;
  const countryIdentifiers = useMemo(() =>
    localState.businessIdentifiers.map((id): CountryIdentifier => ({
      key: id.type,
      label: id.display_name,
      placeholder: id.example ?? `Enter ${id.display_name}`,
      maxLength: undefined,
      isRequired: false,
    })),
    [localState.businessIdentifiers],
  );

  const dialog = usePartyDialog({
    isOpen, onClose, onSuccess, orgId, ledgerId, partyType, editingParty,
    existingPartyCodes: stablePartyCodes, countryIdentifiers, storageKey,
  });

  usePartyDialogEffects({
    isOpen, editingParty, partyType, existingPartyCodes: stablePartyCodes,
    orgId, ledgerId,
    baseCurrency: dialog.baseCurrency, formData: dialog.formData,
    setFormData: dialog.setFormData, setErrors: dialog.setErrors,
    ...localState.setters,
    setDuplicateEntities: dialog.setDuplicateEntities,
    setDuplicateEntitiesParties: dialog.setDuplicateEntitiesParties,
    setSelectedEntity: dialog.setSelectedEntity,
    setShowDuplicateWarning: dialog.setShowDuplicateWarning,
    loadFromLocalStorage: dialog.loadFromLocalStorage,
    saveToLocalStorage: dialog.saveToLocalStorage,
    checkDuplicates: dialog.checkDuplicates,
    isCountriesLoaded: localState.isCountriesLoaded,
    selectedEntity: dialog.selectedEntity,
  });

  return { dialog, countryIdentifiers };
}

export function PartyDialog(props: PartyDialogProps): JSX.Element {
  const { isOpen, onClose, editingParty, orgId } = props;
  const localState = usePartyDialogLocalState();
  const [availableRoles, setAvailableRoles] = useState<RoleOption[] | undefined>(undefined);
  useRoleDefinitions(orgId, setAvailableRoles);

  const { dialog, countryIdentifiers } = usePartyDialogWiring(props, localState);
  const isDirty = (dialog.formData.entityName?.trim() ?? '') !== '';

  return (
    <>
      <Modal
        bodyClassName="p-0 overflow-y-auto"
        footer={<DialogFooter dialog={dialog} editingParty={!!editingParty} onClose={onClose} />}
        isDirty={isDirty}
        isOpen={isOpen}
        size="2xl"
        title={dialog.title}
        onClose={onClose}
      >
        <div className="el-p-xl flex flex-col el-gap-lg overflow-x-hidden">
          {localState.isLoadingCountries && <LoadingSkeleton />}
          {!localState.isLoadingCountries && (
            <PartyDialogSections
              availableRoles={availableRoles}
              billingCountry={localState.billingCountry}
              countryIdentifiers={countryIdentifiers}
              dialog={dialog}
              editingParty={editingParty}
            />
          )}
        </div>
      </Modal>
      <CurrencyConfirmDialog
        currencyCode={dialog.formData.defaultCurrency || dialog.baseCurrency}
        entityLabel={dialog.titleLabel}
        isLoading={dialog.isSaving}
        isOpen={dialog.isCurrencyConfirmVisible}
        onClose={() => dialog.setShowCurrencyConfirm(false)}
        onConfirm={() => { void dialog.handleSave(); }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Inline micro-components
// ---------------------------------------------------------------------------

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="el-space-y-lg animate-pulse">
      <div className="h-4 bg-muted rounded w-1/4" />
      <div className="el-space-y-sm">
        <div className="h-10 bg-muted rounded" />
        <div className="h-10 bg-muted rounded" />
        <div className="h-10 bg-muted rounded" />
      </div>
      <div className="h-4 bg-muted rounded w-1/4 el-mt-lg" />
      <div className="el-space-y-sm">
        <div className="h-10 bg-muted rounded" />
        <div className="h-10 bg-muted rounded" />
      </div>
      <div className="text-center text-sm text-muted-foreground mt-4">
        Loading countries data...
      </div>
    </div>
  );
}

interface DialogFooterProps {
  dialog: ReturnType<typeof usePartyDialog>;
  editingParty: boolean;
  onClose: () => void;
}

function DialogFooter({ dialog, editingParty, onClose }: DialogFooterProps): JSX.Element {
  return (
    <div className="el-bleed flex justify-between items-center">
      <div>
        {!editingParty && (
          <Button disabled={dialog.isSaving} size="sm" variant="ghost" onClick={dialog.handleReset}>
            Reset Form
          </Button>
        )}
      </div>
      <div className="flex el-gap-sm">
        <Button disabled={dialog.isSaving} size="sm" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={dialog.isSaving || dialog.isCheckingDuplicates}
          size="sm"
          onClick={() => { void dialog.handleSave(); }}
        >
          {(() => {
            if (dialog.isSaving) { return 'Saving...' }
            return editingParty ? 'Update' : 'Create'
          })()}
        </Button>
      </div>
    </div>
  );
}

interface ContactInfoSectionProps {
  formData: ReturnType<typeof usePartyDialog>['formData'];
  setFormData: ReturnType<typeof usePartyDialog>['setFormData'];
  errors: Record<string, string>;
}

function ContactInfoSection({ formData, setFormData, errors }: ContactInfoSectionProps): JSX.Element {
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Contact Information
      </h3>
      <div className="grid grid-cols-2 el-gap-field el-mt-sm">
        <FormField compact label="Email" {...(errors.email ? { error: errors.email } : {})}>
          <Input
            placeholder="email@example.com"
            type="email"
            value={formData.email ?? ''}
            onChange={e => setFormData(prev => ({ ...prev, email: e.target.value }))}
          />
        </FormField>
        <FormField compact label="Phone">
          <PhoneInput
            placeholder="Phone number"
            value={formData.phone ?? null}
            onChange={value => setFormData(prev => ({ ...prev, phone: value }))}
          />
        </FormField>
        <FormField compact label="Mobile">
          <PhoneInput
            placeholder="Mobile number"
            value={formData.mobile ?? null}
            onChange={value => setFormData(prev => ({ ...prev, mobile: value }))}
          />
        </FormField>
        <FormField compact label="Website">
          <Input
            placeholder="https://example.com"
            type="url"
            value={formData.website ?? ''}
            onChange={e => setFormData(prev => ({ ...prev, website: e.target.value }))}
          />
        </FormField>
      </div>
    </div>
  );
}

interface PaymentTermsSectionProps {
  dialog: ReturnType<typeof usePartyDialog>;
  editingParty: boolean;
  // True only when the party already has a currency. Currency is set-once:
  // editable while empty (incl. legacy currency-less parties), locked once set.
  currencyLocked: boolean;
}

function CreditFields({ dialog }: { dialog: ReturnType<typeof usePartyDialog> }): JSX.Element {
  return (
    <>
      <FormField compact label="Credit Days">
        <Input
          min="0"
          placeholder="30"
          type="number"
          value={dialog.formData.creditDays}
          onChange={e => dialog.setFormData(prev => ({ ...prev, creditDays: parseInt(e.target.value) || 0 }))}
        />
      </FormField>
      <FormField compact label="Credit Limit">
        <Input
          min="0"
          placeholder="0"
          type="number"
          value={dialog.formData.creditLimit}
          onChange={e => dialog.setFormData(prev => ({ ...prev, creditLimit: parseInt(e.target.value) || 0 }))}
        />
      </FormField>
    </>
  );
}

function CurrencyAndOpeningFields({ dialog, editingParty, currencyLocked }: PaymentTermsSectionProps): JSX.Element {
  // Once set, currency is immutable. While empty (new party or legacy
  // currency-less party) it must be chosen — surface the required-field error.
  const setOnceHelperText = editingParty ? 'Set once — cannot be changed afterwards' : undefined;
  const currencyHelperText = currencyLocked
    ? 'Cannot be changed after creation'
    : setOnceHelperText;
  return (
    <>
      <FormField
        compact
        helperText={currencyHelperText}
        label="Default Currency"
        {...(dialog.errors.defaultCurrency ? { error: dialog.errors.defaultCurrency } : {})}
      >
        <Select
          disabled={currencyLocked}
          options={dialog.currencyOptions}
          value={dialog.formData.defaultCurrency}
          onChange={value => dialog.setFormData(prev => ({ ...prev, defaultCurrency: value }))}
        />
      </FormField>
      {!editingParty && (
        <p className="text-xs text-muted-foreground">
          Opening balances are entered in one place — the <span className="font-medium text-foreground">Opening Balances</span> screen — which lists this party&apos;s ledgers once the party is created.
        </p>
      )}
    </>
  );
}

function PaymentTermsSection({ dialog, editingParty, currencyLocked }: PaymentTermsSectionProps): JSX.Element {
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground el-section-heading el-mb-sm border-b border-border">
        Payment Terms
      </h3>
      <div className="grid grid-cols-2 el-gap-field el-mt-sm">
        <CreditFields dialog={dialog} />
        <CurrencyAndOpeningFields currencyLocked={currencyLocked} dialog={dialog} editingParty={editingParty} />
      </div>
    </div>
  );
}
